import { MobXProviderContext } from "mobx-react";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { DataView } from "primereact/dataview";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toolbar } from "primereact/toolbar";
import { RouterAddress } from "../../../constants/RouterAddress";
import { SHOW_RESIDENT_SCHEDULES } from "../../../constants/uiFlags";
import { appMutedTextStyle, renderSurfaceHeader } from "../../../components/general/uiSurfaceStyles";
import { useResponsive } from "../../../responsive/ResponsiveProvider";

const formatLosAngelesDateTime = (value?: string) => {
  if (!value) return "Not set";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString("en-US", { timeZone: "America/Los_Angeles", dateStyle: "medium", timeStyle: "short" });
};

const statusSeverity = (status: string) => {
  if (status === "completed") return "success";
  if (status === "missed") return "danger";
  if (status === "pending") return "warning";
  if (status === "upcoming" || status === "in_progress") return "info";
  return "secondary";
};

const CareActivityEmptyState = ({ homeName, caregiver = false }: { homeName: string; caregiver?: boolean }) => (
  <>
    <div className="flex flex-column align-items-center text-center">
      <div className="font-semibold text-2xl text-900">{caregiver ? "No care activity yet" : "Set up resident care tracking"}</div>
      <div className="text-color-secondary mt-2" style={{ maxWidth: "44rem" }}>
        {caregiver
          ? `No resident care activity has been recorded for ${homeName} yet. eMAR, ADL, behavior, incident, daily progress, and Entry Tracker entries will appear here as they are recorded.`
          : `There is no Care Activity to show for ${homeName} yet. Set up any of the care tools below for your residents, and this dashboard will summarize their activity in one place.`}
      </div>
    </div>
    {!caregiver ? <div className="mt-4 text-center">
      <div className="font-semibold text-900">Start by setting up:</div>
      <ul className="mt-2 mb-0 pl-5 text-color-secondary" style={{ display: "inline-block", maxWidth: "100%", lineHeight: 1.8, textAlign: "left" }}>
        <li>Resident eMAR medication charting</li>
        <li>ADL tracking</li>
        <li>Behavior notes</li>
        <li>Incident reports</li>
        <li>Daily progress notes</li>
        <li>Entry Tracker In/Out records</li>
      </ul>
    </div> : null}
    <div className="flex align-items-start justify-content-center gap-2 mt-3 text-color-secondary text-sm text-center">
      <i className="pi pi-info-circle mt-1" aria-hidden="true" />
      <span>This dashboard provides a summary across all residents in this home.</span>
    </div>
  </>
);

export const CareActivityPageContent = ({ sharedMode = false }: { sharedMode?: boolean }) => {
  const { homeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const mobile = useResponsive().isMobile;
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const caregiver = String(authStore?.getUserRoleType?.() || "").trim().toLowerCase() === "caregiver";
  const [homeName, setHomeName] = useState("Facility");
  const [items, setItems] = useState<any[]>([]);
  const [residentId, setResidentId] = useState("");
  const [activityType, setActivityType] = useState("all");
  const [dateKey, setDateKey] = useState("");
  const [nextToken, setNextToken] = useState("");
  const [previousToken, setPreviousToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedActivity, setSelectedActivity] = useState<any | null>(null);
  const loadActivityRef = useRef<((options?: { append?: boolean; previous?: boolean }) => Promise<void>) | null>(null);

  const loadActivity = async (options?: { append?: boolean; previous?: boolean }) => {
    if (!homeId) return;
    setLoading(true);
    setErrorMessage("");
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getFacilityCareActivity(homeId, {
        residentId,
        activityType,
        dateKey,
        limit: 50,
        recentHours: options?.append || options?.previous ? undefined : 1,
        nextToken: options?.previous ? undefined : (options?.append ? nextToken : undefined),
        previousToken: options?.previous ? previousToken : undefined,
      });
      const nextItems = (Array.isArray(response?.items) ? response.items : [])
        .filter((item: any) => SHOW_RESIDENT_SCHEDULES || item?.activityType !== "schedule");
      setItems((currentItems) => options?.append || options?.previous
        ? options.previous ? [...nextItems, ...currentItems] : [...currentItems, ...nextItems]
        : nextItems);
      setNextToken(String(response?.nextToken || ""));
      setPreviousToken(String(response?.previousToken || ""));
    } catch (error: any) {
      setErrorMessage(error?.message || "Unable to load Care Activity.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setResidentId(searchParams.get("residentId") || "");
    setActivityType(searchParams.get("activityType") || "all");
    setDateKey(searchParams.get("dateKey") || "");
  }, [location.search]);

  useEffect(() => {
    loadActivityRef.current = loadActivity;
  });

  useEffect(() => {
    const handleRefresh = () => {
      void loadActivityRef.current?.();
    };
    window.addEventListener("care-activity:refresh", handleRefresh);
    return () => window.removeEventListener("care-activity:refresh", handleRefresh);
  }, []);

  useEffect(() => {
    if (!homeId) return;
    void (async () => {
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getBusinessHomeDetails(homeId);
        setHomeName(String(response?.businessName || response?.name || "Facility"));
      } catch {
        setHomeName("Facility");
      }
    })();
  }, [backendAPIStore, homeId]);

  useEffect(() => {
    setItems([]);
    setNextToken("");
    setPreviousToken("");
    void loadActivity();
  }, [homeId, residentId, activityType, dateKey]);

  const viewActivity = (activity: any) => {
    const id = String(activity?.residentId || "");
    if (!id) return;
    if (activity.activityType === "emar") navigate(sharedMode ? RouterAddress.getSharedResidentEmarPagePath(homeId || "", id) : RouterAddress.getProviderResidentEmarPagePath(id, homeId));
    if (activity.activityType === "adl") navigate(sharedMode ? RouterAddress.getSharedResidentAdlPagePath(homeId || "", id) : RouterAddress.getProviderResidentAdlPagePath(id, homeId));
    if (activity.activityType === "behavior") navigate(sharedMode ? RouterAddress.getSharedResidentDailyBehaviorLogPagePath(homeId || "", id) : RouterAddress.getProviderResidentDailyBehaviorLogPagePath(id, undefined, homeId));
    if (activity.activityType === "incident") navigate(sharedMode ? RouterAddress.getSharedResidentIncidentReportPagePath(homeId || "", id) : RouterAddress.getProviderResidentIncidentReportPagePath(id, undefined, homeId));
    if (activity.activityType === "daily_note") navigate(sharedMode ? RouterAddress.getSharedResidentDailyNotesPagePath(homeId || "", id) : RouterAddress.getProviderResidentDailyNotesPagePath(id, undefined, homeId));
    if (activity.activityType === "schedule") navigate(RouterAddress.getResidentSchedulesPagePath(homeId || "", id));
  };

  const residentBody = (activity: any) => <span>{activity.residentName}</span>;
  const activityBody = (activity: any) => <div style={{ minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }}><div style={{ fontWeight: 700 }}>{activity.title}</div>{activity.description ? <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>{activity.description}</div> : null}{detailSummary(activity) ? <div style={{ marginTop: "0.25rem", color: "#475569", fontSize: "0.8rem" }}>{detailSummary(activity)}</div> : null}</div>;
  const detailSummary = (activity: any) => {
    const detailParts: string[] = [];
    const parameterSummary = activity?.chartParameters && Object.keys(activity.chartParameters).length
      ? Object.entries(activity.chartParameters).map(([key, value]) => `${key}: ${typeof value === "object" ? JSON.stringify(value) : String(value)}`).join(" | ")
      : "";
    if (activity?.level) detailParts.push(`Level: ${activity.level}`);
    if (activity?.value) detailParts.push(`Value: ${activity.value}`);
    if (activity?.dosage) detailParts.push(`Dosage: ${activity.dosage}`);
    if (activity?.route) detailParts.push(`Route: ${activity.route}`);
    if (parameterSummary) detailParts.push(`Parameters: ${parameterSummary}`);
    if (activity?.notes) detailParts.push(activity.notes);
    if (activity?.medicationNote) detailParts.push(`Medication note: ${activity.medicationNote}`);
    if (activity?.prnNote) detailParts.push(`PRN note: ${activity.prnNote}`);
    if (activity?.prnDetails && Object.values(activity.prnDetails).some((value: any) => String(value || "").trim())) {
      const prnSummary = Object.entries(activity.prnDetails).filter(([, value]) => String(value || "").trim()).map(([key, value]) => `${key}: ${value}`).join(" | ");
      detailParts.push(`PRN details: ${prnSummary}`);
    }
    if (activity?.followUpDetails) detailParts.push(`Follow-up: ${activity.followUpDetails}`);
    if (activity?.actionTaken) detailParts.push(`Action: ${activity.actionTaken}`);
    return detailParts.join(" • ");
  };
  const statusBody = (activity: any) => activity.activityType === "behavior" ? null : <Tag value={activity.statusLabel} severity={statusSeverity(activity.status) as any} />;
  const timeBody = (activity: any) => <span>{formatLosAngelesDateTime(activity.occurredAt)}</span>;
  const isNoteActivity = (activity: any) => ["behavior", "daily_note", "incident", "resident_movement"].includes(String(activity?.activityType || ""));
  const openActivity = (activity: any) => {
    if (isNoteActivity(activity)) {
      setSelectedActivity(activity);
      return;
    }
    viewActivity(activity);
  };
  const activityActionLabel = (activity: any) => {
    if (activity.activityType === "adl") return "Open ADL";
    if (activity.activityType === "emar") return "Open eMAR";
    if (activity.activityType === "behavior") return "View Behavior";
    if (activity.activityType === "daily_note") return "View Progress Note";
    if (activity.activityType === "incident") return "View Incident";
    if (activity.activityType === "resident_movement") return "View Entry";
    return "Open Activity";
  };
  const activityActionIcon = (activity: any) => isNoteActivity(activity) ? "pi pi-eye" : "pi pi-external-link";
  const completeSchedule = async (activity: any, action: "complete" | "skip") => {
    if (!activity?.residentId || !activity?.scheduleId) return;
    try {
      await backendAPIStore.init();
      const dateKey = String(activity?.dateKey || new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date(activity.occurredAt)));
      const request = { residentId: activity.residentId, scheduleId: activity.scheduleId, dateKey };
      if (action === "complete") await backendAPIStore.completeResidentSchedule(request);
      else await backendAPIStore.skipResidentSchedule(request);
      await loadActivity();
    } catch (error: any) {
      setErrorMessage(error?.message || `Unable to ${action} resident schedule.`);
    }
  };
  const renderActivityAction = (activity: any) => activity.activityType === "schedule" ? (
    <div className="flex gap-1 flex-wrap justify-content-end">
      {activity.status !== "completed" && activity.status !== "skipped" ? <Button type="button" label="Complete" icon="pi pi-check" size="small" severity="success" outlined rounded onClick={() => void completeSchedule(activity, "complete")} /> : null}
      {activity.status !== "completed" && activity.status !== "skipped" ? <Button type="button" label="Skip" icon="pi pi-forward" size="small" severity="secondary" outlined rounded onClick={() => void completeSchedule(activity, "skip")} /> : null}
      <Button type="button" label="Open" icon="pi pi-external-link" size="small" outlined rounded onClick={() => openActivity(activity)} />
    </div>
  ) : <Button type="button" label={activityActionLabel(activity)} icon={activityActionIcon(activity)} size="small" outlined rounded onClick={() => openActivity(activity)} />;
  const renderMobileActivityRow = (activity: any) => (
    <div key={activity.activityId} className="flex align-items-start gap-2" style={{ padding: "0.7rem 0.25rem", borderBottom: "1px solid #e2e8f0" }}>
      <div style={{ width: "2rem", flex: "0 0 2rem", color: "#64748b", fontWeight: 700, paddingTop: "0.15rem" }}>#{Math.max(0, items.findIndex((item: any) => item.activityId === activity.activityId)) + 1}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="flex align-items-center gap-2 flex-wrap" style={{ color: "#64748b", fontSize: "0.76rem" }}>
          <span>{formatLosAngelesDateTime(activity.occurredAt)}</span>
          <span>•</span>
          <span>{activity.residentName}</span>
          <span>•</span>
          <span>{activity.staffName || "Staff"}</span>
        </div>
        <div style={{ marginTop: "0.2rem", fontWeight: 700, color: "#0f172a", overflowWrap: "anywhere" }}>
          {activity.title}
          <span style={{ marginLeft: "0.45rem", color: "#64748b", fontSize: "0.76rem", fontWeight: 600 }}>{activity.activityTypeLabel}</span>
        </div>
        {detailSummary(activity) ? <div style={{ marginTop: "0.2rem", color: "#475569", fontSize: "0.78rem", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{detailSummary(activity)}</div> : null}
      </div>
      <div className="flex flex-column align-items-end gap-1" style={{ flex: "0 0 auto" }}>
        {activity.activityType !== "behavior" ? <Tag value={activity.statusLabel} severity={statusSeverity(activity.status) as any} /> : null}
        {activity.activityType === "schedule" ? renderActivityAction(activity) : <Button type="button" icon={activityActionIcon(activity)} aria-label={activityActionLabel(activity)} tooltip={activityActionLabel(activity)} tooltipOptions={{ position: "left" }} size="small" text rounded onClick={() => openActivity(activity)} />}
      </div>
    </div>
  );
  const activityDetailRows = selectedActivity
    ? [
      ["Charted by", selectedActivity.staffName],
      ["Notes", selectedActivity.notes],
      ["Medication note", selectedActivity.medicationNote],
      ["PRN note", selectedActivity.prnNote],
      ["Follow-up details", selectedActivity.followUpDetails],
      ["Details", selectedActivity.details],
      ["Action taken", selectedActivity.actionTaken],
      ["Resident response", selectedActivity.residentResponse],
      ["Value", selectedActivity.value],
    ].filter(([, value]) => String(value || "").trim())
    : [];
  const enteredDetailRows = selectedActivity?.enteredDetails
    ? Object.entries(selectedActivity.enteredDetails)
    : activityDetailRows;
  const formatDetailLabel = (label: string) => String(label || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (value) => value.toUpperCase());

  return (
    <div className="container-fluid p-3">
      <Toolbar
        start={<div>{renderSurfaceHeader("Care Activity", `A unified view of every resident’s eMAR, ADL, progress notes, incident reports, behavior entries, and Entry Tracker records at ${homeName}.`, "pi pi-list-check")}</div>}
      />
      <Card className="mt-3">
        {errorMessage ? <Message severity="error" text={errorMessage} className="mb-3" /> : null}
        {previousToken ? (
          <div className="flex justify-content-center mb-3">
            <Button type="button" label="Load Previous" icon="pi pi-chevron-up" outlined rounded disabled={loading} onClick={() => void loadActivity({ previous: true })} />
          </div>
        ) : null}
        {loading && !items.length ? <div className="flex justify-content-center p-5"><ProgressSpinner /></div> : !items.length && !errorMessage ? (
          <CareActivityEmptyState homeName={homeName} caregiver={caregiver} />
        ) : mobile ? (
          <DataView value={items} itemTemplate={renderMobileActivityRow} emptyMessage="No Care Activity found." />
        ) : (
          <DataTable value={items} dataKey="activityId" stripedRows showGridlines resizableColumns columnResizeMode="fit" responsiveLayout="stack" breakpoint="768px" emptyMessage="No Care Activity found.">
            <Column header="#" body={(_: any, options: any) => options.rowIndex + 1} style={{ width: "4rem" }} />
            <Column field="occurredAt" header="Time" body={timeBody} sortable style={{ minWidth: "10rem" }} />
            <Column field="residentName" header="Resident" body={residentBody} sortable style={{ minWidth: "10rem" }} />
            <Column field="activityTypeLabel" header="Type" style={{ minWidth: "8rem" }} />
            <Column header="Activity" body={activityBody} style={{ minWidth: "20rem" }} />
            <Column header="Charted by" body={(activity: any) => <span style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{activity.staffName || "Staff"}</span>} style={{ minWidth: "10rem" }} />
            <Column header="Status" body={statusBody} style={{ minWidth: "8rem" }} />
            <Column header="" body={renderActivityAction} bodyStyle={{ textAlign: "right" }} style={{ minWidth: "9rem" }} />
          </DataTable>
        )}
        {nextToken ? (
          <div className="flex justify-content-center gap-2 flex-wrap mt-3">
            <Button type="button" label="Load More" icon="pi pi-chevron-right" iconPos="right" outlined rounded disabled={loading} onClick={() => void loadActivity({ append: true })} />
          </div>
        ) : null}
      </Card>
      <Dialog
        visible={Boolean(selectedActivity)}
        modal
        header={selectedActivity ? `${selectedActivity.activityTypeLabel}: ${selectedActivity.title}` : "Activity details"}
        style={{ width: "min(42rem, 94vw)" }}
        onHide={() => setSelectedActivity(null)}
        footer={<Button type="button" label="Close" outlined rounded onClick={() => setSelectedActivity(null)} />}
      >
        {selectedActivity ? (
          <div className="flex flex-column gap-3">
            <div className="flex align-items-center gap-2 flex-wrap">
              <Tag value={selectedActivity.residentName} severity="info" />
              <Tag value={selectedActivity.statusLabel} severity={statusSeverity(selectedActivity.status) as any} />
              <span style={appMutedTextStyle}>{formatLosAngelesDateTime(selectedActivity.occurredAt)}</span>
            </div>
            {enteredDetailRows.length ? enteredDetailRows.map(([label, value]) => (
              <div key={label}>
                <div style={{ fontWeight: 700, color: "#334155", marginBottom: "0.25rem", textTransform: "capitalize" }}>{formatDetailLabel(String(label))}</div>
                <div style={{ whiteSpace: "pre-wrap", color: "#475569" }}>{typeof value === "object" ? JSON.stringify(value) : String(value)}</div>
              </div>
            )) : <Message severity="info" text="No additional details were recorded for this activity." />}
            {selectedActivity.chartParameters && Object.keys(selectedActivity.chartParameters).length ? (
              <div>
                <div style={{ fontWeight: 700, color: "#334155", marginBottom: "0.45rem" }}>Chart parameters</div>
                <div className="flex flex-column gap-2">
                  {Object.entries(selectedActivity.chartParameters).map(([key, value]) => (
                    <div key={key} className="flex justify-content-between gap-3" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "0.35rem" }}>
                      <span style={{ color: "#64748b" }}>{key}</span>
                      <span style={{ color: "#0f172a", fontWeight: 600, textAlign: "right" }}>{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </Dialog>
    </div>
  );
};

const CareActivityPage = () => <CareActivityPageContent />;

export default CareActivityPage;
